import { useState, useMemo } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Bird, Bug, Shield, Send, Clock, Calculator, ChevronDown, Check, Phone, Mail, MapPin, Home, Rat, Plus, Minus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Header } from "../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png";

export default function QuoteBuilderPage() {
  // Client info
  const [client, setClient] = useState({ name: "", email: "", phone: "", address: "" });
  
  // Bird proofing
  const [includeBird, setIncludeBird] = useState(true);
  const [panels, setPanels] = useState("");
  const [storeys, setStoreys] = useState("single");
  const [pigeonDuration, setPigeonDuration] = useState("<6m");
  const [addGutterCleaning, setAddGutterCleaning] = useState(false);
  const [addPanelWash, setAddPanelWash] = useState(false);
  const [addPressureWash, setAddPressureWash] = useState(false);
  const [addBirdMite, setAddBirdMite] = useState(false);
  
  // Pest control
  const [includePest, setIncludePest] = useState(false);
  const [treatmentType, setTreatmentType] = useState("standard");
  const [serviceFrequency, setServiceFrequency] = useState("onceoff");
  const [bedrooms, setBedrooms] = useState("3");
  const [houseType, setHouseType] = useState("house");
  const [pestStoreys, setPestStoreys] = useState("single");
  const [addRodent, setAddRodent] = useState(false);
  
  // Termite
  const [includeTermite, setIncludeTermite] = useState(false);
  
  // Discount
  const [extraDiscount, setExtraDiscount] = useState(0);
  
  // Sales rep
  const [salesRep, setSalesRep] = useState("");
  
  // Notes
  const [notes, setNotes] = useState("");
  
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Calculate bird proofing price
  const birdEstimate = useMemo(() => {
    if (!includeBird || !panels) return null;
    const panelCount = parseInt(panels);
    if (isNaN(panelCount) || panelCount <= 0) return null;

    let meshTotal = 0;
    if (panelCount <= 16) meshTotal = panelCount * 42;
    else if (panelCount <= 24) meshTotal = (16 * 42) + ((panelCount - 16) * 45.50);
    else meshTotal = (16 * 42) + (8 * 45.50) + ((panelCount - 24) * 49);
    meshTotal = Math.max(550, Math.round(meshTotal));

    const storeyMultiplier = storeys === "three" ? 1.3 : storeys === "two" ? 1.15 : 1;
    const storeyAddition = storeys === "three" ? 250 : storeys === "two" ? 150 : 0;
    meshTotal = Math.round(meshTotal * storeyMultiplier) + storeyAddition;

    // Individual cleaning service prices (scale with panels + storey)
    const storeyCleanMult = storeys === "three" ? 1.40 : storeys === "two" ? 1.20 : 1;
    const storeyFlat = storeys === "three" ? 80 : storeys === "two" ? 50 : 0;
    const panelWashLevy = storeys === "three" ? 80 : storeys === "two" ? 50 : 0;
    const gutterCleaningPrice = Math.round((60 + panelCount * 8) * storeyCleanMult + storeyFlat);
    const panelWashPrice = Math.round((25 + panelCount * 5) * storeyCleanMult + storeyFlat + panelWashLevy);
    const pressureWashPrice = Math.round((50 + panelCount * 9) * storeyCleanMult + storeyFlat);

    const selectedCleaningCount = [addGutterCleaning, addPanelWash, addPressureWash].filter(Boolean).length;
    const isCleaningBundle = selectedCleaningCount >= 2;
    const rawCleaningTotal = (addGutterCleaning ? gutterCleaningPrice : 0) + (addPanelWash ? panelWashPrice : 0) + (addPressureWash ? pressureWashPrice : 0);
    const cleaningBundleDiscount = isCleaningBundle ? Math.round(rawCleaningTotal * 0.20) : 0;
    const cleaningTotal = rawCleaningTotal - cleaningBundleDiscount;

    const birdMitePrice = addBirdMite ? 200 : 0;

    return { meshTotal, gutterCleaningPrice, panelWashPrice, pressureWashPrice, selectedCleaningCount, isCleaningBundle, rawCleaningTotal, cleaningBundleDiscount, cleaningTotal, birdMitePrice, panelCount };
  }, [includeBird, panels, storeys, addGutterCleaning, addPanelWash, addPressureWash, addBirdMite]);

  // Calculate pest price
  const pestEstimate = useMemo(() => {
    if (!includePest) return null;
    const bedroomNum = parseInt(bedrooms);
    let basePrice = houseType === "unit" ? 155 : 198;
    if (bedroomNum >= 5) basePrice += 40;
    else if (bedroomNum >= 4) basePrice += 20;
    if (pestStoreys === "double") basePrice += 25;
    if (treatmentType === "comprehensive") basePrice += 60;
    if (serviceFrequency === "sixmonthly") basePrice = Math.round(basePrice * 0.85);

    let rodentPrice = 0;
    if (addRodent) {
      rodentPrice = 175;
      if (bedroomNum <= 2) rodentPrice -= 25;
      else if (bedroomNum >= 5) rodentPrice += 25;
      if (pestStoreys === "double") rodentPrice += 20;
      if (treatmentType === "comprehensive") rodentPrice += 35;
    }

    return { basePrice, rodentPrice };
  }, [includePest, bedrooms, houseType, pestStoreys, treatmentType, serviceFrequency, addRodent]);

  // Calculate termite price
  const termiteEstimate = useMemo(() => {
    if (!includeTermite) return null;
    const bedroomNum = parseInt(bedrooms);
    let price = 150;
    if (bedroomNum >= 5) price += 70;
    else if (bedroomNum >= 4) price += 40;
    else if (bedroomNum >= 3) price += 20;
    if (pestStoreys === "double") price += 60;
    return { price };
  }, [includeTermite, bedrooms, pestStoreys]);

  // Calculate totals
  const totals = useMemo(() => {
    let subtotal = 0;
    if (birdEstimate) subtotal += birdEstimate.meshTotal + birdEstimate.cleaningTotal + birdEstimate.birdMitePrice;
    if (pestEstimate) subtotal += pestEstimate.basePrice + pestEstimate.rodentPrice;
    if (termiteEstimate) subtotal += termiteEstimate.price;

    const launchDiscount = Math.round(subtotal * 0.10);
    const afterLaunch = subtotal - launchDiscount;
    const extraDiscountAmount = Math.round(afterLaunch * (extraDiscount / 100));
    const finalTotal = afterLaunch - extraDiscountAmount;
    const totalDiscount = launchDiscount + extraDiscountAmount;

    return { subtotal, launchDiscount, afterLaunch, extraDiscountAmount, finalTotal, totalDiscount };
  }, [birdEstimate, pestEstimate, termiteEstimate, extraDiscount]);

  const handleSend = async () => {
    if (!client.name || !client.email) {
      toast.error("Please enter at least the customer's name and email");
      return;
    }
    if (!birdEstimate && !pestEstimate && !termiteEstimate) {
      toast.error("Please configure at least one service");
      return;
    }

    setSending(true);
    try {
      const payload = {
        client,
        bird: birdEstimate ? { panels: parseInt(panels), storeys, pigeonDuration, meshTotal: birdEstimate.meshTotal, gutterCleaning: addGutterCleaning, gutterCleaningPrice: birdEstimate.gutterCleaningPrice, panelWash: addPanelWash, panelWashPrice: birdEstimate.panelWashPrice, pressureWash: addPressureWash, pressureWashPrice: birdEstimate.pressureWashPrice, cleaningBundleDiscount: birdEstimate.cleaningBundleDiscount, birdMite: addBirdMite, birdMitePrice: birdEstimate.birdMitePrice } : null,
        pest: pestEstimate ? { treatmentType, serviceFrequency, bedrooms, houseType, storeys: pestStoreys, basePrice: pestEstimate.basePrice, rodent: addRodent, rodentPrice: pestEstimate.rodentPrice } : null,
        termite: termiteEstimate ? { price: termiteEstimate.price } : null,
        totals: { subtotal: totals.subtotal, launchDiscount: totals.launchDiscount, extraDiscount, extraDiscountAmount: totals.extraDiscountAmount, finalTotal: totals.finalTotal },
        salesRep,
        notes
      };

      await axios.post(`${API}/api/send-quote`, payload);
      toast.success("Quote sent successfully!");
      setSent(true);
    } catch (err) {
      toast.error("Failed to send quote: " + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setClient({ name: "", email: "", phone: "", address: "" });
    setPanels(""); setStoreys("single"); setPigeonDuration("<6m");
    setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); setAddBirdMite(false);
    setIncludePest(false); setIncludeTermite(false);
    setSalesRep(""); setNotes("");
    setSent(false);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Pest Free" className="h-12" />
            <div>
              <h1 className="text-xl font-bold text-white">Quote Builder</h1>
              <p className="text-sm text-slate-400">Internal quoting tool</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Clock className="w-3.5 h-3.5" />
            <span>10% launch discount auto-applied</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {sent ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-[#7ABC32]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#7ABC32]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Quote Sent!</h2>
            <p className="text-slate-400 mb-8">Quote has been emailed to {client.email}</p>
            <Button onClick={resetForm} className="bg-[#7ABC32] hover:bg-[#6AAD28] text-white">
              Create Another Quote
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Customer Info */}
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-800" data-testid="customer-info-section">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#7ABC32]" /> Customer Details
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 text-sm">Name *</Label>
                    <Input value={client.name} onChange={e => setClient({...client, name: e.target.value})} placeholder="Customer name" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="quote-name" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Email *</Label>
                    <Input type="email" value={client.email} onChange={e => setClient({...client, email: e.target.value})} placeholder="customer@email.com" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="quote-email" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Phone</Label>
                    <Input value={client.phone} onChange={e => setClient({...client, phone: e.target.value})} placeholder="04XX XXX XXX" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="quote-phone" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Address</Label>
                    <Input value={client.address} onChange={e => setClient({...client, address: e.target.value})} placeholder="Street address" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="quote-address" />
                  </div>
                </div>
              </div>

              {/* Bird Proofing */}
              <div className={`bg-slate-900 rounded-xl border ${includeBird ? 'border-[#5BC0DE]' : 'border-slate-800'} overflow-hidden`} data-testid="bird-proofing-section">
                <button onClick={() => setIncludeBird(!includeBird)} className="w-full p-4 flex items-center justify-between text-left">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${includeBird ? 'bg-[#5BC0DE]/20' : 'bg-slate-800'}`}>
                      <Bird className={`w-5 h-5 ${includeBird ? 'text-[#5BC0DE]' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <h2 className="font-bold text-white">Solar Panel Bird Proofing</h2>
                      <p className="text-xs text-slate-400">Stainless steel mesh with epoxy resin coating</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${includeBird ? 'border-[#5BC0DE] bg-[#5BC0DE]' : 'border-slate-600'}`}>
                    {includeBird && <Check className="w-4 h-4 text-white" />}
                  </div>
                </button>
                {includeBird && (
                  <div className="px-5 pb-5 space-y-5 border-t border-slate-800 pt-5">
                    
                    {/* Step 1 - Panel count */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-[#5BC0DE] text-white text-xs font-bold flex items-center justify-center">1</span>
                        <Label className="text-white font-medium">How many solar panels?</Label>
                      </div>
                      <Input type="number" value={panels} onChange={e => setPanels(e.target.value)} placeholder="e.g. 20" className="bg-slate-800 border-slate-700 text-white max-w-[200px] text-lg py-5" data-testid="quote-panels" />
                    </div>

                    {/* Step 2 - Building height */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-[#5BC0DE] text-white text-xs font-bold flex items-center justify-center">2</span>
                        <Label className="text-white font-medium">Building height</Label>
                      </div>
                      <div className="flex gap-2">
                        {[
                          { val: "single", label: "1 Storey" },
                          { val: "two", label: "2 Storey" },
                          { val: "three", label: "3+ Storey" }
                        ].map(s => (
                          <button key={s.val} onClick={() => setStoreys(s.val)} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${storeys === s.val ? 'bg-[#5BC0DE] text-white' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-500'}`}>
                            {s.label}
                          </button>
                        ))}
                      </div>
                      {storeys === "two" && <p className="text-xs text-slate-400 mt-1.5 italic">*2 technicians required — harness system & higher access</p>}
                      {storeys === "three" && <p className="text-xs text-slate-400 mt-1.5 italic">*Boom lift hire, harness system & 2 technicians required</p>}
                    </div>

                    {/* Step 3 - Nesting duration */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-[#5BC0DE] text-white text-xs font-bold flex items-center justify-center">3</span>
                        <Label className="text-white font-medium">How long have pigeons been nesting?</Label>
                      </div>
                      <div className="flex gap-3">
                        <button onClick={() => { setPigeonDuration("<6m"); setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); }} className={`flex-1 py-3 rounded-lg text-center transition-all ${pigeonDuration === "<6m" ? 'bg-[#5BC0DE] text-white ring-2 ring-[#5BC0DE]/50' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-500'}`}>
                          <div className="text-sm font-medium">Less than 6 months</div>
                          <div className="text-xs mt-0.5 opacity-70">Light nesting</div>
                        </button>
                        <button onClick={() => setPigeonDuration(">6m")} className={`flex-1 py-3 rounded-lg text-center transition-all ${pigeonDuration === ">6m" ? 'bg-[#5BC0DE] text-white ring-2 ring-[#5BC0DE]/50' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-500'}`}>
                          <div className="text-sm font-medium">More than 6 months</div>
                          <div className="text-xs mt-0.5 opacity-70">Heavy nesting</div>
                        </button>
                      </div>
                    </div>

                    {/* Live price preview */}
                    {birdEstimate && (
                      <div className="bg-[#5BC0DE]/10 border border-[#5BC0DE]/30 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-[#5BC0DE] font-medium text-sm">Mesh Install ({birdEstimate.panelCount} panels)</span>
                          <span className="text-white font-bold text-lg">${birdEstimate.meshTotal}</span>
                        </div>
                      </div>
                    )}

                    {/* Optional Add-ons */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Plus className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-400 font-medium uppercase tracking-wide">Optional Add-ons</span>
                      </div>
                      {/* Cleaning Services */}
                      {birdEstimate && birdEstimate.selectedCleaningCount > 0 && birdEstimate.selectedCleaningCount < 2 && (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
                          <span className="text-amber-400 text-xs font-medium">Select 2+ cleaning services to save 20%</span>
                        </div>
                      )}
                      {birdEstimate && birdEstimate.isCleaningBundle && (
                        <div className="bg-[#7ABC32]/10 border border-[#7ABC32]/30 rounded-lg px-3 py-2 flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#7ABC32]" />
                          <span className="text-[#7ABC32] text-xs font-semibold">Bundle discount applied — saving ${birdEstimate.cleaningBundleDiscount}!</span>
                        </div>
                      )}
                      <div className="space-y-2">
                        {[
                          { key: "gutter", active: addGutterCleaning, toggle: () => setAddGutterCleaning(!addGutterCleaning), label: "Gutter Cleaning", desc: "Full gutter flush & debris removal", price: birdEstimate?.gutterCleaningPrice, details: ["Remove all debris from gutters", "Flush & clear downpipes", "Dispose of waste material"] },
                          { key: "panel", active: addPanelWash, toggle: () => setAddPanelWash(!addPanelWash), label: "Solar Panel Wash", desc: "Surface wash & clean panel tops", price: birdEstimate?.panelWashPrice, details: ["Water-fed broom clean on panel tops", "Remove bird droppings & residue", "Restore panel efficiency"] },
                          { key: "pressure", active: addPressureWash, toggle: () => setAddPressureWash(!addPressureWash), label: "Pressure Wash", desc: "Pressure wash & sanitise area", price: birdEstimate?.pressureWashPrice, details: ["High-pressure wash around panels", "Sanitise & deodorise affected areas", "Remove staining from roof surface"] },
                        ].map(opt => (
                          <button key={opt.key} onClick={opt.toggle} data-testid={`cleaning-${opt.key}`} className={`w-full p-3 rounded-lg text-left transition-all ${opt.active ? 'bg-[#7ABC32]/15 border-2 border-[#7ABC32]' : 'bg-slate-800/50 border-2 border-slate-700/50 hover:border-slate-600'}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${opt.active ? 'border-[#7ABC32] bg-[#7ABC32]' : 'border-slate-600'}`}>
                                {opt.active && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span className="text-sm font-semibold text-white whitespace-nowrap">{opt.label}</span>
                              <span className="text-xs text-slate-400 hidden sm:inline">— {opt.desc}</span>
                              {opt.price && <span className="text-[#7ABC32] font-bold text-sm ml-auto">${opt.price}</span>}
                            </div>
                            {opt.active && (
                              <ul className="mt-2 ml-8 space-y-0.5">
                                {opt.details.map((d, i) => <li key={i} className="flex items-center gap-1.5 text-xs text-slate-400"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />{d}</li>)}
                              </ul>
                            )}
                          </button>
                        ))}
                      </div>
                      {/* Bird Mite */}
                      <button onClick={() => setAddBirdMite(!addBirdMite)} data-testid="addon-bird-mite" className={`w-full p-4 rounded-lg text-left transition-all ${addBirdMite ? 'bg-[#7ABC32]/15 border-2 border-[#7ABC32]' : 'bg-slate-800/50 border-2 border-slate-700/50 hover:border-slate-600'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-white">Bird Mite Treatment</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[#7ABC32] font-bold text-sm">$200</span>
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${addBirdMite ? 'border-[#7ABC32] bg-[#7ABC32]' : 'border-slate-600'}`}>
                              {addBirdMite && <Check className="w-3 h-3 text-white" />}
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400">Treat mite infestation around panels</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Pest Control */}
              <div className={`bg-slate-900 rounded-xl border ${includePest ? 'border-[#E74C3C]' : 'border-slate-800'} overflow-hidden`} data-testid="pest-control-section">
                <button onClick={() => setIncludePest(!includePest)} className="w-full p-4 flex items-center justify-between text-left">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${includePest ? 'bg-[#E74C3C]/20' : 'bg-slate-800'}`}>
                      <Bug className={`w-5 h-5 ${includePest ? 'text-[#E74C3C]' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <h2 className="font-bold text-white">Pest Control</h2>
                      <p className="text-xs text-slate-400">General pest treatment + optional rodent</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${includePest ? 'border-[#E74C3C] bg-[#E74C3C]' : 'border-slate-600'}`}>
                    {includePest && <Check className="w-4 h-4 text-white" />}
                  </div>
                </button>
                {includePest && (
                  <div className="px-5 pb-5 space-y-5 border-t border-slate-800 pt-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white font-medium text-sm mb-2 block">Treatment Type</Label>
                        <div className="flex gap-3">
                          {[
                            { val: "standard", desc: "Interior & exterior spray" },
                            { val: "comprehensive", desc: "Full treatment + roof void" }
                          ].map(t => (
                            <button key={t.val} onClick={() => setTreatmentType(t.val)} className={`flex-1 py-3 px-3 rounded-lg text-center transition-all ${treatmentType === t.val ? 'bg-[#E74C3C] text-white ring-2 ring-[#E74C3C]/50' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-500'}`}>
                              <div className="text-sm font-medium capitalize">{t.val}</div>
                              <div className="text-xs mt-0.5 opacity-70">{t.desc}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-white font-medium text-sm mb-2 block">Frequency</Label>
                        <div className="flex gap-3">
                          <button onClick={() => setServiceFrequency("onceoff")} className={`flex-1 py-3 rounded-lg text-center transition-all ${serviceFrequency === "onceoff" ? 'bg-[#E74C3C] text-white ring-2 ring-[#E74C3C]/50' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-500'}`}>
                            <div className="text-sm font-medium">Once-off</div>
                          </button>
                          <button onClick={() => setServiceFrequency("sixmonthly")} className={`flex-1 py-3 rounded-lg text-center transition-all ${serviceFrequency === "sixmonthly" ? 'bg-[#E74C3C] text-white ring-2 ring-[#E74C3C]/50' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-500'}`}>
                            <div className="text-sm font-medium">6-Monthly</div>
                            <div className="text-xs mt-0.5 opacity-70">15% off per visit</div>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-white font-medium text-sm mb-2 block">Bedrooms</Label>
                        <div className="flex gap-2">
                          {["2", "3", "4", "5"].map(b => (
                            <button key={b} onClick={() => setBedrooms(b)} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${bedrooms === b ? 'bg-[#E74C3C] text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
                              {b}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-white font-medium text-sm mb-2 block">Property Type</Label>
                        <div className="flex gap-2">
                          <button onClick={() => setHouseType("house")} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${houseType === "house" ? 'bg-[#E74C3C] text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>House</button>
                          <button onClick={() => setHouseType("unit")} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${houseType === "unit" ? 'bg-[#E74C3C] text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>Unit</button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white font-medium text-sm mb-2 block">Storeys</Label>
                        <div className="flex gap-2">
                          <button onClick={() => setPestStoreys("single")} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${pestStoreys === "single" ? 'bg-[#E74C3C] text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>1 Storey</button>
                          <button onClick={() => setPestStoreys("double")} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${pestStoreys === "double" ? 'bg-[#E74C3C] text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>2 Storey</button>
                        </div>
                      </div>
                    </div>

                    {/* Live price + optional rodent */}
                    {pestEstimate && (
                      <div className="bg-[#E74C3C]/10 border border-[#E74C3C]/30 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-[#E74C3C] font-medium text-sm">Pest Control ({treatmentType})</span>
                          <span className="text-white font-bold text-lg">${pestEstimate.basePrice}</span>
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Plus className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-400 font-medium uppercase tracking-wide">Optional Add-on</span>
                      </div>
                      <button onClick={() => setAddRodent(!addRodent)} className={`w-full p-4 rounded-lg text-left transition-all ${addRodent ? 'bg-[#7ABC32]/15 border-2 border-[#7ABC32]' : 'bg-slate-800/50 border-2 border-slate-700/50 hover:border-slate-600'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-semibold text-white">Rodent Treatment</span>
                            <p className="text-xs text-slate-400 mt-0.5">Bait stations & monitoring around property</p>
                          </div>
                          <div className="flex items-center gap-3">
                            {pestEstimate && <span className="text-[#7ABC32] font-bold text-sm">${pestEstimate.rodentPrice || "—"}</span>}
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${addRodent ? 'border-[#7ABC32] bg-[#7ABC32]' : 'border-slate-600'}`}>
                              {addRodent && <Check className="w-3 h-3 text-white" />}
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Termite */}
              <div className={`bg-slate-900 rounded-xl border ${includeTermite ? 'border-[#D2691E]' : 'border-slate-800'} overflow-hidden`} data-testid="termite-section">
                <button onClick={() => setIncludeTermite(!includeTermite)} className="w-full p-4 flex items-center justify-between text-left">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${includeTermite ? 'bg-[#D2691E]/20' : 'bg-slate-800'}`}>
                      <Shield className={`w-5 h-5 ${includeTermite ? 'text-[#D2691E]' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <h2 className="font-bold text-white">Termite Inspection</h2>
                      <p className="text-xs text-slate-400">Annual termite inspection</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${includeTermite ? 'border-[#D2691E] bg-[#D2691E]' : 'border-slate-600'}`}>
                    {includeTermite && <Check className="w-4 h-4 text-white" />}
                  </div>
                </button>
                {includeTermite && termiteEstimate && (
                  <div className="px-4 pb-4 border-t border-slate-800 pt-4">
                    <div className="bg-slate-800 rounded-lg p-3 text-sm text-slate-300">
                      Termite Inspection: ${termiteEstimate.price}
                    </div>
                  </div>
                )}
              </div>

              {/* Sales Rep */}
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-800" data-testid="sales-rep-section">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-400" /> Sales Rep (Commission)
                </h2>
                <div>
                  <Label className="text-slate-300 text-sm">Rep Name</Label>
                  <Input value={salesRep} onChange={e => setSalesRep(e.target.value)} placeholder="Door knocker / sales rep name" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="quote-sales-rep" />
                  <p className="text-xs text-slate-500 mt-1">This name will be recorded for commission tracking</p>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
                <Label className="text-slate-300 text-sm">Additional Notes (included in email)</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional info for the customer..." className="bg-slate-800 border-slate-700 text-white mt-2 min-h-[80px]" data-testid="quote-notes" />
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-slate-900 rounded-xl border border-slate-800 sticky top-6" data-testid="quote-summary">
                <div className="p-5 border-b border-slate-800">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-[#7ABC32]" /> Quote Summary
                  </h3>
                </div>

                <div className="p-5 space-y-3">
                  {/* Section 1: Bird Proofing */}
                  {birdEstimate && (
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Bird Proofing</p>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#5BC0DE] font-medium">Mesh Install ({birdEstimate.panelCount} panels)</span>
                        <span className="text-white">${birdEstimate.meshTotal}</span>
                      </div>
                      <div className="flex justify-between text-sm text-slate-400 pl-3">
                        <span>Basic Nest Removal</span>
                        <span className="text-[#7ABC32] text-xs">Included</span>
                      </div>
                      <div className="flex justify-between text-sm pt-1 border-t border-slate-700/50">
                        <span className="text-[#5BC0DE] font-semibold text-xs">Proofing Total</span>
                        <span className="text-[#5BC0DE] font-semibold">${birdEstimate.meshTotal}</span>
                      </div>
                    </div>
                  )}

                  {/* Section 2: Cleaning Services */}
                  {birdEstimate && (addGutterCleaning || addPanelWash || addPressureWash) && (
                    <div className="space-y-1 pt-2 border-t border-slate-700/50">
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Cleaning Services</p>
                      {addGutterCleaning && (
                        <div className="flex justify-between text-sm text-slate-400 pl-3">
                          <span>Gutter Cleaning</span>
                          <span>${birdEstimate.gutterCleaningPrice}</span>
                        </div>
                      )}
                      {addPanelWash && (
                        <div className="flex justify-between text-sm text-slate-400 pl-3">
                          <span>Solar Panel Wash</span>
                          <span>${birdEstimate.panelWashPrice}</span>
                        </div>
                      )}
                      {addPressureWash && (
                        <div className="flex justify-between text-sm text-slate-400 pl-3">
                          <span>Pressure Wash / Sanitise</span>
                          <span>${birdEstimate.pressureWashPrice}</span>
                        </div>
                      )}
                      {birdEstimate.isCleaningBundle && (
                        <div className="flex justify-between text-sm text-[#7ABC32] pl-3">
                          <span>Bundle Discount (20%)</span>
                          <span>-${birdEstimate.cleaningBundleDiscount}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm pt-1 border-t border-slate-700/50">
                        <span className="text-[#7ABC32] font-semibold text-xs">Cleaning Total</span>
                        <span className="text-[#7ABC32] font-semibold">${birdEstimate.cleaningTotal}</span>
                      </div>
                    </div>
                  )}

                  {/* Section 3: Add-Ons */}
                  {(addBirdMite || pestEstimate || termiteEstimate) && (
                    <div className="space-y-1 pt-2 border-t border-slate-700/50">
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Add-Ons</p>
                      {addBirdMite && (
                        <div className="flex justify-between text-sm text-slate-400 pl-3">
                          <span>Bird Mite Treatment</span>
                          <span>$200</span>
                        </div>
                      )}
                      {pestEstimate && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-[#E74C3C] font-medium pl-3">Pest Control ({treatmentType})</span>
                            <span className="text-white">${pestEstimate.basePrice}</span>
                          </div>
                          {addRodent && (
                            <div className="flex justify-between text-sm text-slate-400 pl-6">
                              <span>+ Rodent Treatment</span>
                              <span>${pestEstimate.rodentPrice}</span>
                            </div>
                          )}
                        </>
                      )}
                      {termiteEstimate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-[#D2691E] font-medium pl-3">Termite Inspection</span>
                          <span className="text-white">${termiteEstimate.price}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {!birdEstimate && !pestEstimate && !termiteEstimate && (
                    <p className="text-slate-500 text-sm text-center py-4">Select services above to build quote</p>
                  )}

                  {totals.subtotal > 0 && (
                    <>
                      <div className="border-t border-slate-700 pt-3">
                        <div className="flex justify-between text-sm text-slate-400">
                          <span>Subtotal</span>
                          <span>${totals.subtotal}</span>
                        </div>
                        <div className="flex justify-between text-sm text-[#7ABC32]">
                          <span>10% Launch Discount</span>
                          <span>-${totals.launchDiscount}</span>
                        </div>
                        {extraDiscount > 0 && (
                          <div className="flex justify-between text-sm text-amber-400">
                            <span>Extra {extraDiscount}% Discount</span>
                            <span>-${totals.extraDiscountAmount}</span>
                          </div>
                        )}
                      </div>

                      {/* Extra Discount Control */}
                      <div className="border-t border-slate-700 pt-3">
                        <Label className="text-slate-400 text-xs mb-2 block">Additional Discount</Label>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setExtraDiscount(Math.max(0, extraDiscount - 5))} className="w-8 h-8 rounded bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white" data-testid="discount-minus">
                            <Minus className="w-4 h-4" />
                          </button>
                          <div className="flex-1 text-center">
                            <span className="text-xl font-bold text-white">{extraDiscount}%</span>
                          </div>
                          <button onClick={() => setExtraDiscount(Math.min(30, extraDiscount + 5))} className="w-8 h-8 rounded bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white" data-testid="discount-plus">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex justify-between mt-1">
                          {[0, 5, 10, 15, 20, 25].map(d => (
                            <button key={d} onClick={() => setExtraDiscount(d)} className={`text-xs px-1.5 py-0.5 rounded ${extraDiscount === d ? 'bg-[#7ABC32] text-white' : 'text-slate-500 hover:text-slate-300'}`}>
                              {d}%
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="border-t border-slate-700 pt-3">
                        <div className="flex justify-between items-baseline">
                          <span className="text-lg font-bold text-white">Total</span>
                          <span className="text-2xl font-bold text-[#7ABC32]">${totals.finalTotal}</span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">Prices include GST</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="p-5 border-t border-slate-800">
                  <Button onClick={handleSend} disabled={sending || !client.name || !client.email || totals.subtotal === 0} className="w-full bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 text-lg font-semibold" data-testid="send-quote-btn">
                    {sending ? "Sending..." : <><Send className="w-5 h-5 mr-2" /> Send Quote to Customer</>}
                  </Button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Quote will be emailed to {client.email || "customer"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
